import { useCallback, useMemo } from "react"
import { InfiniteData, useInfiniteQuery, useQueryClient } from "react-query"
import { PROOF_OF_OWNERSHIP_STATUS } from "~constants/proofOfOwnership"
import ProofOfOwnershipService from "~services/ProofOfOwnershipService"
import { ProofOfOwnership } from "~shapes/proofOfOwnership"

const PAGE_SIZE = 25

interface PooPage {
  data: ProofOfOwnership[]
  next?: string
}

const useProofsOfOwnership = (
  userId?: string,
  status?: PROOF_OF_OWNERSHIP_STATUS,
  enabled = true,
  // When omitted, no `sort` param is sent and the backend's default order is
  // used (newest first).
  sort?: string,
  // "always" forces a refetch on every mount so screens like create-portfolio
  // show POOs created elsewhere as soon as the operator navigates back, instead
  // of serving the (still within staleTime) cached list.
  refetchOnMount: boolean | "always" = true,
) => {
  const queryClient = useQueryClient()
  const queryKey = [
    "user-proofs-of-ownership",
    userId ?? "",
    status ?? "all",
    sort ?? "",
  ]

  const query = useInfiniteQuery<PooPage>(
    queryKey,
    ({ pageParam }) =>
      ProofOfOwnershipService.getProofsOfOwnership(
        status,
        userId,
        PAGE_SIZE,
        pageParam,
        sort,
      ),
    {
      enabled,
      staleTime: 2 * 60 * 1000,
      refetchOnMount,
      getNextPageParam: lastPage => lastPage.next || undefined,
    },
  )

  const pages = query.data?.pages
  const data = useMemo(() => {
    const allItems = pages?.flatMap(p => p.data) ?? []
    const seen = new Set<string>()
    const filteredByStatus = status
      ? allItems.filter(item => item.state === status)
      : allItems

    return filteredByStatus.filter(item => {
      if (seen.has(item.id)) return false
      seen.add(item.id)
      return true
    })
  }, [pages, status])

  // react-query refetches EVERY loaded page on a plain refetch(). After a
  // mutation (validate/discard/invalidate) the operator restarts from the top of
  // the queue, so we don't need pages 2..N — replaying them fires one request per
  // loaded page (each with its `next=` cursor) and slows the screen to a crawl.
  // Trim the cache back to the first page, then refetch just that one. This also
  // drops any stale later page that might still list the just-mutated item.
  const refetch = useCallback(async () => {
    const existing = queryClient.getQueryData<InfiniteData<PooPage>>(queryKey)
    if (existing && existing.pages.length > 1) {
      queryClient.setQueryData<InfiniteData<PooPage>>(queryKey, {
        pages: existing.pages.slice(0, 1),
        pageParams: existing.pageParams.slice(0, 1),
      })
    }
    return query.refetch()
    // queryKey members are the deps; query.refetch is stable per query instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, query.refetch, userId, status, sort])

  return {
    isFetching: query.isFetching,
    data,
    refetch,
    isError: query.isError,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage ?? false,
    isFetchingNextPage: query.isFetchingNextPage,
  }
}

export default useProofsOfOwnership
